import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const C = ["#2563EB","#F97316","#16A34A","#92400E","#6B7280","#d0d0d0","#DC2626","#aaaaaa","#CA8A04","#7C3AED","#DB2777","#0891B2"];
const N = ["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"];
const SP_PAL = ["#2563EB","#F97316","#16A34A","#7C3AED","#DB2777","#0891B2","#CA8A04","#DC2626"];

function cH(abs){ return C[(abs-1)%12]; }
function nH(abs){ return N[(abs-1)%12]; }
function absH(buf,hilo){ return (parseInt(buf)-1)*12+parseInt(hilo); }

export default function DiagramaRed({ fibras, fusiones, splitters, fibrasSalida }) {
  const svgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [sel,  setSel]  = useState(null);

  const soloSalIds = new Set((fibrasSalida||[]).map(f=>String(f.id)));

  useEffect(()=>{
    if(!svgRef.current || !fibras || fibras.length===0) return;

    const container = svgRef.current.parentElement;
    const W = container.clientWidth || 900;
    const H = 580;

    d3.select(svgRef.current).selectAll("*").remove();
    const svg = d3.select(svgRef.current).attr("width",W).attr("height",H);
    const g   = svg.append("g").attr("class","canvas");

    const ROW_H    = 22;
    const BUF_GAP  = 14;
    const PAD_TOP  = 70;
    const COL_ENT  = 160;
    const COL_SAL  = W - 160;
    const SP_CX    = W / 2;

    const fibrasOrd = [...fibras].sort((a,b)=>parseInt(b.tipo_hilos)-parseInt(a.tipo_hilos));
    const fEnt = fibrasOrd.find(f=>!soloSalIds.has(String(f.id)));
    const fSal = fibrasOrd.find(f=>soloSalIds.has(String(f.id))) || fEnt;

    if(!fEnt) return;

    const totalHilos = parseInt(fEnt.tipo_hilos)||12;
    const numBufs    = Math.ceil(totalHilos/12);

    const hiloY = (buf,hilo)=>{
      const abs = absH(buf,hilo);
      return PAD_TOP + (abs-1)*ROW_H + (Math.floor((abs-1)/12))*BUF_GAP;
    };

    const totalDiagH = PAD_TOP + totalHilos*ROW_H + numBufs*BUF_GAP + 60;
    svg.attr("height", Math.max(H, totalDiagH));

    const zoomBeh = d3.zoom().scaleExtent([0.1,8]).on("zoom",(e)=>{
      g.attr("transform",e.transform);
      setZoom(+(e.transform.k.toFixed(2)));
    });
    svg.call(zoomBeh);

    // Fondos por buffer
    for(let b=1;b<=numBufs;b++){
      const hilos = Math.min(12,totalHilos-(b-1)*12);
      const yTop  = hiloY(b,1) - ROW_H*0.4;
      const yBot  = hiloY(b,hilos) + ROW_H*0.6;
      const color = C[(b-1)%12];
      g.append("rect")
        .attr("x",30).attr("y",yTop)
        .attr("width",W-60).attr("height",yBot-yTop)
        .attr("rx",4).attr("fill",`${color}08`).attr("stroke",`${color}22`).attr("stroke-width",1);
      g.append("text").attr("x",34).attr("y",yTop+12)
        .attr("font-size",8).attr("fill",color).attr("font-weight","700").text(`B${b}`);
    }

    // Etiquetas columnas
    g.append("text").attr("x",COL_ENT).attr("y",PAD_TOP-38).attr("text-anchor","middle")
      .attr("font-size",11).attr("fill","#C4B5FD").attr("font-weight","700").text(fEnt.nombre+` (${fEnt.tipo_hilos}h)`);
    g.append("text").attr("x",COL_ENT).attr("y",PAD_TOP-22).attr("text-anchor","middle")
      .attr("font-size",9).attr("fill","#FBBF24").text("← ENTRADA");
    g.append("text").attr("x",COL_SAL).attr("y",PAD_TOP-38).attr("text-anchor","middle")
      .attr("font-size",11).attr("fill","#34D399").attr("font-weight","700")
      .text(fSal&&fSal.id!==fEnt.id ? fSal.nombre+` (${fSal.tipo_hilos}h)` : fEnt.nombre+` (${fEnt.tipo_hilos}h)`);
    g.append("text").attr("x",COL_SAL).attr("y",PAD_TOP-22).attr("text-anchor","middle")
      .attr("font-size",9).attr("fill","#34D399").text("SALIDA →");
    g.append("text").attr("x",SP_CX).attr("y",PAD_TOP-22).attr("text-anchor","middle")
      .attr("font-size",9).attr("fill","#6B7280").text("ZONA DE FUSIONES");

    // Dibujar hilos — puntos entrada y salida
    for(let b=1;b<=numBufs;b++){
      const hilos = Math.min(12,totalHilos-(b-1)*12);
      for(let h=1;h<=hilos;h++){
        const abs = absH(b,h);
        const y   = hiloY(b,h);
        const col = cH(abs);

        // Punto entrada
        g.append("circle").attr("cx",COL_ENT).attr("cy",y).attr("r",4).attr("fill",col)
          .attr("id",`ent-${b}-${h}`);
        g.append("text").attr("x",COL_ENT-8).attr("y",y+4)
          .attr("text-anchor","end").attr("font-size",8).attr("fill",col).text(`H${abs}`);

        // Punto salida (apagado por defecto)
        g.append("circle").attr("cx",COL_SAL).attr("cy",y).attr("r",4).attr("fill",col).attr("opacity",0.25)
          .attr("id",`sal-${b}-${h}`);
        g.append("text").attr("x",COL_SAL+8).attr("y",y+4)
          .attr("font-size",8).attr("fill",col).attr("opacity",0.25)
          .attr("id",`sal-lbl-${b}-${h}`).text(`H${abs}`);
      }
    }

    // ── SPLITTERS ──
    (splitters||[]).forEach((sp,si)=>{
      const spCol  = SP_PAL[si%SP_PAL.length];
      const numSal = parseInt(sp.tipo.split("x")[1]);
      const entFus = (fusiones||[]).find(f=>f.tipo==="splitter_entrada"&&String(f.splitter_id)===String(sp.id));
      if(!entFus) return;

      const bE    = parseInt(entFus.buffer_entrada);
      const hE    = parseInt(entFus.hilo_entrada);
      const yEnt  = hiloY(bE,hE);
      const colEnt= cH(absH(bE,hE));

      const salidas = (fusiones||[])
        .filter(f=>f.tipo==="splitter_salida"&&String(f.splitter_id)===String(sp.id))
        .sort((a,b)=>parseInt(a.splitter_salida_num)-parseInt(b.splitter_salida_num));

      const SP_H   = Math.max(numSal*ROW_H, 80);
      const spX    = SP_CX - 40;
      const spYtop = yEnt - SP_H/2;
      const spYbot = yEnt + SP_H/2;

      // Triángulo splitter
      g.append("polygon")
        .attr("points",`${spX},${yEnt} ${spX+80},${spYtop} ${spX+80},${spYbot}`)
        .attr("fill",`${spCol}20`).attr("stroke",spCol).attr("stroke-width",2)
        .style("cursor","pointer")
        .on("click",()=>setSel({tipo:"splitter",sp,color:spCol}));

      g.append("text").attr("x",spX+40).attr("y",yEnt-5)
        .attr("text-anchor","middle").attr("font-size",10).attr("fill",spCol).attr("font-weight","700")
        .text(sp.tipo).style("pointer-events","none");
      g.append("text").attr("x",spX+40).attr("y",yEnt+9)
        .attr("text-anchor","middle").attr("font-size",8).attr("fill",spCol).attr("opacity",0.8)
        .text(`SP${si+1}`).style("pointer-events","none");

      // Línea entrada → punta
      g.append("line")
        .attr("x1",COL_ENT).attr("y1",yEnt)
        .attr("x2",spX).attr("y2",yEnt)
        .attr("stroke",colEnt).attr("stroke-width",2);

      // Punto F
      g.append("circle").attr("cx",spX).attr("cy",yEnt).attr("r",6).attr("fill","#F59E0B");
      g.append("text").attr("x",spX).attr("y",yEnt+4)
        .attr("text-anchor","middle").attr("font-size",7).attr("fill","#000").attr("font-weight","700").text("F");

      // Marcar entrada como usada
      g.append("circle").attr("cx",COL_ENT).attr("cy",yEnt).attr("r",5)
        .attr("fill",colEnt).attr("stroke","#F59E0B").attr("stroke-width",2);

      // ── RUTEO INTELIGENTE SALIDAS ──
      // Ordenar salidas por Y destino para evitar cruces
      const salidasInfo = salidas.map((sal,idx)=>{
        const bS   = parseInt(sal.buffer_salida);
        const hS   = parseInt(sal.hilo_salida);
        const dirS = sal.dir_salida||"adelante";
        const yDst = hiloY(bS,hS);
        return {...sal, idx, bS, hS, dirS, yDst, colS: cH(absH(bS,hS)), numSal: sal.splitter_salida_num};
      });

      const salAd = [...salidasInfo.filter(s=>s.dirS==="adelante")].sort((a,b)=>a.yDst-b.yDst);
      const salDev= [...salidasInfo.filter(s=>s.dirS==="devolver")].sort((a,b)=>a.yDst-b.yDst);
      const allOrd= [...salAd, ...salDev];

      // Distribuir puntos en base del triángulo de arriba a abajo
      allOrd.forEach((sal,i)=>{
        const baseY = spYtop + i*(spYbot-spYtop)/(allOrd.length-1||1);
        const {bS,hS,dirS,yDst,colS} = sal;

        // Punto base triángulo
        g.append("circle").attr("cx",spX+80).attr("cy",baseY).attr("r",4).attr("fill","#F59E0B");
        g.append("text").attr("x",spX+87).attr("y",baseY+4)
          .attr("font-size",7).attr("fill","#CA8A04").text(`${sal.numSal||i+1}`);

        if(dirS==="adelante"){
          if(Math.abs(baseY-yDst)<1){
            // Línea recta directa — sin escalera
            g.append("line")
              .attr("x1",spX+80).attr("y1",baseY)
              .attr("x2",COL_SAL).attr("y2",yDst)
              .attr("stroke",colS).attr("stroke-width",1.5);
          } else {
            // Escalera mínima — columna bypass separada por índice
            const xByp = spX + 80 + 12 + i*9;
            g.append("path")
              .attr("d",`M${spX+80},${baseY} L${xByp},${baseY} L${xByp},${yDst} L${COL_SAL},${yDst}`)
              .attr("fill","none").attr("stroke",colS).attr("stroke-width",1.5);
          }
          // Activar punto salida
          g.select(`#sal-${bS}-${hS}`).attr("opacity",1).attr("fill",colS).attr("r",5);
          g.select(`#sal-lbl-${bS}-${hS}`).attr("opacity",1);
        } else {
          // Devolver — escalera hacia COL_ENT
          const xByp = COL_ENT - 15 - i*9;
          g.append("path")
            .attr("d",`M${spX+80},${baseY} L${spX+85},${baseY} L${spX+85},${yDst} L${xByp},${yDst} L${COL_ENT},${yDst}`)
            .attr("fill","none").attr("stroke",colS).attr("stroke-width",1.5)
            .attr("stroke-dasharray","5,3");
          g.append("circle").attr("cx",COL_ENT).attr("cy",yDst).attr("r",5)
            .attr("fill",colS).attr("stroke","#FBBF24").attr("stroke-width",1.5);
          g.append("text").attr("x",COL_ENT-8).attr("y",yDst+4)
            .attr("text-anchor","end").attr("font-size",7).attr("fill","#FBBF24").text("←");
        }
      });
    });

    // ── FUSIONES HILO-HILO ──
    let hhR=0, hhL=0;
    (fusiones||[]).filter(f=>f.tipo==="hilo_hilo").forEach((fu)=>{
      const bE   = parseInt(fu.buffer_entrada);
      const hE   = parseInt(fu.hilo_entrada);
      const bS   = parseInt(fu.buffer_salida);
      const hS   = parseInt(fu.hilo_salida);
      const dirS = fu.dir_salida||"adelante";
      const colE = cH(absH(bE,hE));
      const yE   = hiloY(bE,hE);
      const yS   = hiloY(bS,hS);

      if(dirS==="adelante"){
        hhR++;
        const xByp = SP_CX+50+hhR*9;
        g.append("path")
          .attr("d",`M${COL_ENT},${yE} L${xByp},${yE} L${xByp},${yS} L${COL_SAL},${yS}`)
          .attr("fill","none").attr("stroke",colE).attr("stroke-width",1.5);
        g.append("circle").attr("cx",xByp).attr("cy",(yE+yS)/2).attr("r",5).attr("fill","#F59E0B");
        g.append("text").attr("x",xByp).attr("y",(yE+yS)/2+4)
          .attr("text-anchor","middle").attr("font-size",6).attr("fill","#000").attr("font-weight","700").text("F");
        g.select(`#sal-${bS}-${hS}`).attr("opacity",1).attr("r",5);
        g.select(`#sal-lbl-${bS}-${hS}`).attr("opacity",1);
      } else {
        hhL++;
        const xByp = COL_ENT-20-hhL*9;
        g.append("path")
          .attr("d",`M${COL_ENT},${yE} L${xByp},${yE} L${xByp},${yS} L${COL_ENT},${yS}`)
          .attr("fill","none").attr("stroke",colE).attr("stroke-width",1.5)
          .attr("stroke-dasharray","5,3");
        g.append("circle").attr("cx",xByp).attr("cy",(yE+yS)/2).attr("r",5).attr("fill","#F59E0B");
        g.append("text").attr("x",xByp).attr("y",(yE+yS)/2+4)
          .attr("text-anchor","middle").attr("font-size",6).attr("fill","#000").attr("font-weight","700").text("F");
      }
    });

    // Fit inicial
    const scale = Math.min(1, (H-40)/totalDiagH);
    svg.call(zoomBeh.transform, d3.zoomIdentity.translate(0,10).scale(scale));

  },[fibras,fusiones,splitters,fibrasSalida]);

  return(
    <div style={{position:"relative",width:"100%",background:"#0d0d1a",borderRadius:8,overflow:"hidden"}}>
      <div style={{position:"absolute",top:8,left:8,zIndex:10,display:"flex",gap:4,alignItems:"center"}}>
        {[["＋","in"],["－","out"],["⊙","reset"]].map(([l,a])=>(
          <button key={a} onClick={()=>{
            const sv=d3.select(svgRef.current);
            const zb=d3.zoom().scaleExtent([0.1,8]).on("zoom",(e)=>{sv.select("g.canvas").attr("transform",e.transform);setZoom(+(e.transform.k.toFixed(2)));});
            if(a==="in")    sv.transition().duration(300).call(zb.scaleBy,1.4);
            if(a==="out")   sv.transition().duration(300).call(zb.scaleBy,0.7);
            if(a==="reset") sv.transition().duration(300).call(zb.transform,d3.zoomIdentity.translate(0,10).scale(0.8));
          }} style={{background:"rgba(30,30,60,0.95)",color:"#C4B5FD",border:"1px solid #374151",borderRadius:4,padding:"3px 9px",fontSize:13,cursor:"pointer",fontWeight:"700"}}>{l}</button>
        ))}
        <span style={{color:"#6B7280",fontSize:9,marginLeft:4}}>{Math.round(zoom*100)}% · Scroll=zoom · Arrastrar=mover</span>
      </div>

      <svg ref={svgRef} style={{width:"100%",display:"block",cursor:"grab",background:"#0d0d1a"}}/>

      {sel&&(
        <div style={{position:"absolute",top:8,right:8,width:180,background:"rgba(15,15,30,0.97)",border:"1px solid #374151",borderRadius:8,padding:"10px",zIndex:20}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:10,color:"#C4B5FD",fontWeight:600}}>Detalle</span>
            <button onClick={()=>setSel(null)} style={{background:"transparent",color:"#6B7280",border:"none",cursor:"pointer",fontSize:11}}>✕</button>
          </div>
          {sel.tipo==="splitter"&&(
            <div style={{fontSize:10,color:"#9CA3AF"}}>
              <p style={{margin:"2px 0",color:sel.color,fontWeight:700}}>{sel.sp.tipo}</p>
              <p style={{margin:"2px 0"}}>Nivel: {sel.sp.nivel}</p>
              <p style={{margin:"2px 0",color:"#6B7280"}}>Pérdida: {{"1x2":3.5,"1x4":7.3,"1x8":10.5,"1x16":13.9,"1x32":17.2}[sel.sp.tipo]||"?"}dB</p>
            </div>
          )}
        </div>
      )}

      <div style={{position:"absolute",bottom:8,left:8,display:"flex",gap:10,fontSize:8,color:"#4B5563",background:"rgba(0,0,0,0.5)",padding:"3px 8px",borderRadius:4}}>
        <span style={{color:"#F59E0B"}}>● F=Fusión</span>
        <span style={{color:"#34D399"}}>━ Adelante →</span>
        <span style={{color:"#FBBF24"}}>╌ Devolver ←</span>
      </div>
    </div>
  );
}
